/**
 * Live-agent handoff — outbound send routing.
 *
 * Pure decision: which wire frame kind should the user's typed message become
 * right now? During an ACTIVE (non-ended) handoff, replies route to the live
 * HubSpot rep (`agent_text`) instead of Sara (`text`) — see Frontend work
 * items 4/5 in docs/plans/2026-07-hubspot-native-handoff.md.
 *
 * Extracted as a standalone pure function (rather than inlined in
 * ChatContext.sendText/retryMessage) so the routing decision is
 * unit-testable without React/RTL — matching this codebase's convention of
 * factoring policy into small pure/tested functions (turn-state.ts,
 * restore-messages.ts) rather than burying it in a component.
 */

import { HandoffPhase, UIState, type HandoffContext } from '@/types/chat';
import { OutboundFrameKind } from './types';

export type SendFrame =
  | { frameKind: typeof OutboundFrameKind.TEXT }
  | { frameKind: typeof OutboundFrameKind.AGENT_TEXT; handoffId: string };

/**
 * Decide the frame for a user-typed send/retry.
 *
 * Routes to AGENT_TEXT only when the UI is actually in HANDOFF mode AND a
 * handoff context exists AND it is in an ACTIVE phase (WAITING/CONNECTED).
 * Deliberately an allow-list, not "anything but ENDED": COLLECTING
 * (Decision #17, 2026-07-hubspot-native-handoff.md — the pre-handoff
 * contact-form gate) must also fall through to TEXT — no HubSpot thread
 * exists yet during that phase (that's the entire point of the gate), so
 * there is nowhere for an agent_text reply to relay to. In practice
 * COLLECTING never coexists with UIState.HANDOFF (the CONTACT_INFO_REQUESTED
 * case in useAssistantConnection.ts deliberately leaves uiState at CHAT), but
 * this keeps the routing decision correct in its own right rather than
 * leaning on that invariant. Any other combination (including a null/ended
 * handoff with HANDOFF uiState — e.g. a stale render mid-transition) falls
 * back to TEXT so a message never silently vanishes into a dead handoff.
 */
export function resolveSendFrame(
  uiState: UIState,
  handoff: HandoffContext | null
): SendFrame {
  if (
    uiState === UIState.HANDOFF &&
    handoff &&
    (handoff.phase === HandoffPhase.WAITING ||
      handoff.phase === HandoffPhase.CONNECTED)
  ) {
    return {
      frameKind: OutboundFrameKind.AGENT_TEXT,
      handoffId: handoff.handoffId,
    };
  }
  return { frameKind: OutboundFrameKind.TEXT };
}
